import { useEffect, useState } from "react";
import axios from "axios";
import { Users, CalendarClock, TrendingUp, Inbox } from "lucide-react";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { AdminNav } from "../components/admin/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { key: "novo", label: "Novo", color: "#00D765" },
  { key: "em_atendimento", label: "Em atendimento", color: "#1AA886" },
  { key: "visita_agendada", label: "Visita agendada", color: "#004C3F" },
  { key: "negociacao", label: "Negociação", color: "#C9A227" },
  { key: "ganho", label: "Ganho", color: "#2F855A" },
  { key: "perdido", label: "Perdido", color: "#9CA3AF" },
];

export default function AdminDashboard() {
  const { user, headers, logout } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/crm/stats`, { headers: headers() }).then((r) => setStats(r.data)).catch(() => setStats({}));
    axios.get(`${API}/users`, { headers: headers() }).then((r) => setUsers(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const userName = (email) => users.find((u) => u.email === email)?.name || email;
  const total = stats?.total || 0;
  const ganhoPct = total ? Math.round(((stats.by_status?.ganho || 0) / total) * 100) : 0;

  const cards = [
    { label: "Contatos totais", value: total, icon: Inbox, testId: "stat-total" },
    { label: "Novos este mês", value: stats?.this_month ?? 0, icon: TrendingUp, testId: "stat-month" },
    { label: "Visitas agendadas", value: stats?.visits_upcoming ?? 0, icon: CalendarClock, testId: "stat-visits" },
    { label: "Taxa de ganho", value: `${ganhoPct}%`, icon: Users, testId: "stat-winrate" },
  ];

  return (
    <main className="mx-auto min-h-screen max-w-7xl px-6 pt-20 pb-24 lg:px-10" data-testid="admin-dashboard">
      <AdminNav user={user} onLogout={logout} />
      <div className="mt-8 mb-10">
        <p className="text-sm font-bold uppercase tracking-[0.25em] text-seed-salad">Dashboard</p>
        <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-seed-deep" data-testid="dashboard-title">
          Visão do funil
        </h1>
      </div>

      {!stats ? (
        <div className="flex justify-center py-16">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-seed-deep/10 border-t-seed-salad" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="dashboard-cards">
            {cards.map((c) => (
              <div key={c.testId} className="rounded-lg bg-white p-6 shadow-sm" data-testid={c.testId}>
                <c.icon size={20} className="text-seed-salad" />
                <p className="mt-3 font-heading text-3xl font-black text-seed-deep">{c.value}</p>
                <p className="mt-1 text-xs font-bold uppercase tracking-wider text-seed-deep/50">{c.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-lg bg-white p-8 shadow-sm" data-testid="dashboard-funnel">
              <h2 className="mb-6 font-heading text-lg font-bold text-seed-deep">Funil por etapa</h2>
              <div className="space-y-4">
                {STAGES.map((s) => {
                  const count = stats.by_status?.[s.key] || 0;
                  const pct = total ? Math.max((count / total) * 100, count ? 6 : 0) : 0;
                  return (
                    <div key={s.key} data-testid={`funnel-${s.key}`}>
                      <div className="mb-1.5 flex justify-between text-xs font-bold">
                        <span className="uppercase tracking-wider text-seed-deep/70">{s.label}</span>
                        <span className="text-seed-deep">{count}</span>
                      </div>
                      <div className="h-3 overflow-hidden rounded-full bg-seed-off">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: s.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-lg bg-white p-8 shadow-sm" data-testid="dashboard-responsible">
                <h2 className="mb-6 font-heading text-lg font-bold text-seed-deep">Contatos por responsável</h2>
                {Object.keys(stats.by_responsible || {}).length === 0 ? (
                  <p className="text-sm text-seed-deep/40">Nenhum contato atribuído ainda.</p>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(stats.by_responsible).map(([email, count]) => {
                      const pct = total ? Math.max((count / total) * 100, 6) : 0;
                      return (
                        <div key={email}>
                          <div className="mb-1.5 flex justify-between text-xs font-bold">
                            <span className="text-seed-deep/70">{userName(email)}</span>
                            <span className="text-seed-deep">{count}</span>
                          </div>
                          <div className="h-3 overflow-hidden rounded-full bg-seed-off">
                            <div className="h-full rounded-full bg-seed-salad transition-all duration-700" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="rounded-lg bg-white p-8 shadow-sm" data-testid="dashboard-interests">
                <h2 className="mb-6 font-heading text-lg font-bold text-seed-deep">Interesses</h2>
                {Object.keys(stats.by_interesse || {}).length === 0 ? (
                  <p className="text-sm text-seed-deep/40">Sem dados de interesse ainda.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(stats.by_interesse).map(([label, count]) => (
                      <span key={label} className="rounded-full border border-seed-salad/40 px-4 py-2 text-xs font-bold text-seed-salad">
                        {label} · {count}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-lg bg-white p-8 shadow-sm" data-testid="dashboard-origins">
                <h2 className="mb-6 font-heading text-lg font-bold text-seed-deep">Origem dos contatos</h2>
                {Object.keys(stats.by_origem || {}).length === 0 ? (
                  <p className="text-sm text-seed-deep/40">Sem dados de origem ainda.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(stats.by_origem).map(([label, count]) => (
                      <span key={label} className="rounded-full bg-seed-deep px-4 py-2 text-xs font-bold text-seed-off">
                        {label} · {count}
                      </span>
                    ))}
                  </div>
                )}
                <p className="mt-4 text-xs leading-relaxed text-seed-deep/40">
                  Dica: compartilhe links com ?utm_source=instagram ou ?utm_source=whatsapp no final do endereço do site para medir cada canal.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </main>
  );
}
